<template>
  <el-config-provider
    :locale="locale"
    data-testid="admin-app"
  >
    <div class="app" data-testid="app-div">
      <div id="widget-load-icon" data-testid="app-div-2"></div>

      <BlockCompany :model-value="isBlockedCompany"  data-testid="app-block-company"/>
      <component v-if="layout" :is="layout" />
      <router-view v-slot="{ Component }" data-testid="app-router-view">
        <transition name="fade">
          <div
            v-if="bootstrapped && getExist"
            class="content-route min-h-full w-full"
           data-testid="app-div-3">
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
      <HelpPopover
        v-if="currentHelpKey && !isHideHelp"
        :current-module="currentHelpKey"
        :visual-state="visualState"
        @update:visual-state="setModuleVisualState(currentHelpKey, $event)"
        @request-hide="isHideConfirmOpen = true"
       data-testid="app-help-popover"/>
      <HelpHideConfirmModal
        v-model="isHideConfirmOpen"
        @confirm="confirmHideHelp"
       data-testid="app-help-hide-confirm-modal"/>
    </div>
  </el-config-provider>
</template>

<script lang="ts" setup>
import { ElConfigProvider } from 'element-plus';
import { getCookie, setElementLocale } from '@/utils/elementPlusLocale.js';
import { computed, onBeforeMount, onMounted, ref, shallowRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { useProgress } from '@/lib/ui/hooks/progress';
import { useSleep } from '@/lib/ui/hooks/sleep';
import { userStore } from '@/store/modules/user.js';
import { useI18n } from 'vue-i18n';
import { useWebsocketHandler } from '@/services/socket/webSocketHandler';
import { companyStore } from '@/store/modules/company.js';
import BlockCompany from '@/components/modals/BlockedModal.vue';
import HelpPopover from '@/components/helpPopover/HelpPopover.vue'
import HelpHideConfirmModal from '@/components/helpPopover/HelpHideConfirmModal.vue'
import { getHelpKeyFromRoute } from '@/constants/helpPages.js'
import { useHelpSettings } from '@/composables/useHelpSettings.js';

import LayoutDefault from '@/layouts/default.vue';
import JwtService from '../src/common/jwt.service';

const router = useRouter();
const route = useRoute();
const UserStore = userStore();
const { locale: i18nLocale } = useI18n();
const { start } = useWebsocketHandler();
const CompanyStore = companyStore();

const auth = useAuthStore();
const progress = useProgress();
const sleep = useSleep();
const bootstrapped = ref(false);
const layout = shallowRef(null);
const isHideConfirmOpen = ref(false);

const {
  isHideHelp,
  getModuleVisualState,
  setModuleVisualState,
  hideHelpGlobally,
} = useHelpSettings();

router.beforeEach((from, to, next) => {
  if (from.fullPath === '/auth/login') {
    if (JwtService.getToken('auth')) {
      next('/');
    }
    UserStore.setExist(true);
  }
  if (from.fullPath === '/auth/recovery') {
    if (JwtService.getToken('auth')) {
      next('/');
    }
    UserStore.setExist(true);
  }

  if (from.fullPath === '/auth/changePassword') {
    UserStore.setExist(true);
  }

  progress.start();
  next();
});

const getExist = computed(() => {
  return UserStore.getExist;
});

const locale = computed(() => {
  const lang = getCookie('PLAY_LANG') ? getCookie('PLAY_LANG') : UserStore.$state.me.lang.key;
  return setElementLocale(lang);
});

const isBlockedCompany = computed(() => {
  return UserStore.getMeActiveCompany?.active_company?.dealer?.blocked;
});

const currentHelpKey = computed(() => getHelpKeyFromRoute(route))

const visualState = computed(() => getModuleVisualState(currentHelpKey.value));

async function confirmHideHelp() {
  isHideConfirmOpen.value = false;
  await hideHelpGlobally();
}

watch(i18nLocale, (newVal) => {
  updateHtmlLang(newVal);
})

const updateHtmlLang = (newLang) => {
  document.documentElement.setAttribute('lang', newLang);
}

onMounted(() => {
  updateHtmlLang(i18nLocale.value);
})

onBeforeMount(async () => {
  try {
    progress.start();
    await Promise.all([router.isReady(), auth.bootstrap(), sleep(500)]);
  } finally {
    progress.finish();
    bootstrapped.value = true;
    router.afterEach(() => {
      progress.finish();
    });

    start();
  }
});
// CompanyStore.count = 0
watch(
  () => route.meta,
  async (meta: { layout }) => {
    const name = meta.layout;

    try {
      await import(`./layouts/${name}.vue`);

      layout.value = LayoutDefault;
    } catch (e) {
      layout.value = '';
    }
  },
  { immediate: true }
);
watch(
  () => route.fullPath,
  () => {
    CompanyStore.count = 0;
  }
);
</script>

<style scoped>
.app {
  display: flex;
  overflow: hidden;
}

.content-route {
  min-height: 100vh;
}
</style>
